// web/src/components/Toast.tsx
//
// Minimal toast system. `showToast(msg, type?)` is a free function any module
// can call. `<ToastHost />` renders the active toast in the bottom-right.

import React, { useEffect, useState } from 'react'
import clsx from 'clsx'

type ToastType = 'info' | 'error' | 'success'
interface ToastState { id: number; message: string; type: ToastType }

let nextId = 0
const listeners = new Set<(t: ToastState) => void>()

export function showToast(message: string, type: ToastType = 'info'): void {
  const t: ToastState = { id: ++nextId, message, type }
  listeners.forEach(fn => fn(t))
}

export const ToastHost: React.FC = () => {
  const [current, setCurrent] = useState<ToastState | null>(null)

  useEffect(() => {
    const handler = (t: ToastState) => {
      setCurrent(t)
      setTimeout(() => {
        setCurrent(prev => (prev?.id === t.id ? null : prev))
      }, 4000)
    }
    listeners.add(handler)
    return () => { listeners.delete(handler) }
  }, [])

  if (!current) return null

  const bg = current.type === 'error'
    ? 'bg-statusRed text-white'
    : current.type === 'success'
      ? 'bg-statusGreen text-white'
      : 'bg-surface text-textPrimary'

  return (
    <div
      role="status"
      className={clsx(
        'pointer-events-auto fixed bottom-lg right-lg z-[60] max-w-xs rounded-md px-md py-sm shadow-lg',
        'animate-[fadeIn_0.2s_ease-out]',
        bg,
      )}
    >
      {current.message}
    </div>
  )
}
